import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useAuthStore } from '@/store/auth.store';
import CreateBatchForm from './components/CreateBatchForm';

export default function DLCBatchesPage() {
  const { t, i18n } = useTranslation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [printingBatchId, setPrintingBatchId] = useState<string | null>(null);
  const apiClient = useApiClient();
  const token = useAuthStore((state) => state.token);

  const handlePrintLabel = async (batchId: string) => {
    try {
      setPrintingBatchId(batchId);
      const response = await fetch(`/api/dlc/batches/${batchId}/label`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch label');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `label-${batchId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Print error:', error);
      alert(t('dlc.printError'));
    } finally {
      setPrintingBatchId(null);
    }
  };

  const { data: preparations = [], isLoading: prepLoading, error: prepError, refetch: refetchPrep } = useQuery({
    queryKey: ['internal-preparations'],
    queryFn: () => apiClient.listInternalPreparations(),
  });

  const { data: batches = [], isLoading: batchLoading, error: batchesError, refetch: refetchBatches } = useQuery({
    queryKey: ['preparation-batches'],
    queryFn: () => apiClient.listBatches(),
  });

  const isLoading = batchLoading || prepLoading;
  const hasError = batchesError || prepError;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <span className="text-2xl">⏱️</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {t('dlc.batches')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('dlc.batchesSubtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading && !hasError && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {hasError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
            <h3 className="text-red-900 font-semibold mb-2">{t('dlc.loadError')}</h3>
            <p className="text-red-700 text-sm mb-4">
              {batchesError ? t('dlc.loadBatchesError') : prepError ? t('dlc.loadPreparationsError') : t('dlc.loadErrorMessage')}
            </p>
            <div className="flex flex-wrap gap-2">
              {batchesError && (
                <button
                  onClick={() => refetchBatches()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
                >
                  {t('dlc.retryBatches')}
                </button>
              )}
              {prepError && (
                <button
                  onClick={() => refetchPrep()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
                >
                  {t('dlc.retryPreparations')}
                </button>
              )}
            </div>
          </div>
        )}

        {!isLoading && !hasError && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-end">
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                disabled={isLoading || !!prepError}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {showCreateForm ? t('dlc.cancelBatch') : t('dlc.newBatch')}
              </button>
            </div>

            {showCreateForm && !prepError && preparations.length > 0 && (
              <CreateBatchForm
                preparations={preparations}
                onSuccess={() => setShowCreateForm(false)}
              />
            )}

            {showCreateForm && prepError && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">{t('dlc.loadPreparationsError')}</p>
              </div>
            )}

            {showCreateForm && preparations.length === 0 && !prepError && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">{t('dlc.noPreparations')}</p>
              </div>
            )}

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {t('dlc.batchesCount', { count: batches.length })}
              </h2>
              {batches.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <p className="text-gray-500">
                    {t('dlc.noBatches')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {batches.map((batch) => {
                    const dlcDate = new Date(batch.dlc);
                    const now = new Date();
                    const hoursUntilDLC = (dlcDate.getTime() - now.getTime()) / (1000 * 60 * 60);

                    let statusColor = 'text-green-600 bg-green-50';
                    if (hoursUntilDLC < 0) statusColor = 'text-red-600 bg-red-50';
                    else if (hoursUntilDLC < 24) statusColor = 'text-amber-600 bg-amber-50';

                    return (
                      <div
                        key={batch.id}
                        className="bg-white rounded-lg shadow p-4 sm:p-6 hover:shadow-lg transition"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                          <div>
                            <h3 className="font-bold text-lg text-gray-900">
                              {batch.internalPreparation?.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {t('dlc.batchLabel')}: <span className="font-mono font-bold">{batch.batchId}</span>
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColor}`}>
                            {batch.status}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">{t('dlc.quantity')}</p>
                            <p className="text-lg font-bold text-gray-900">
                              {batch.quantity} {batch.unit}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">{t('dlc.preparedOn')}</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {new Date(batch.productionDateTime).toLocaleString(i18n.language)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">{t('dlc.expiresAt')}</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {new Date(batch.dlc).toLocaleString(i18n.language)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">{t('dlc.preparedBy')}</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {batch.createdBy?.displayName || batch.createdBy?.email}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => handlePrintLabel(batch.id)}
                            disabled={printingBatchId === batch.id}
                            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {printingBatchId === batch.id ? t('dlc.printing') : t('dlc.printLabel')}
                          </button>
                          <button className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-amber-600 bg-amber-50 rounded hover:bg-amber-100 transition">
                            🗑️ {t('dlc.destroy')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
